import { useState } from "react";
import { ArrowLeft } from "lucide-react";
import { Type } from "@shared/ui/Type";
import { Button } from "@shared/ui/Button";
import { useForm } from "@shared/features/form/useForm";
import { makeFormFields } from "@shared/utils/makeFormFields";
import { ForgotYourPasswordForm } from "./ForgotYourPasswordForm";
import { baseForgotPasswordSchema } from "../../auth.schemas";
import useForgotPasswordMutation from "../../hooks/useForgotPasswordMutation";
import AuthContent from "../AuthContent/AuthContent";

type Props = {
  onCancel: () => void;
  onError?: () => void;
  onSuccess?: (data: any) => void;
};

export function ForgotPasswordContainer({
  onCancel,
  onSuccess,
  onError,
}: Props) {
  const [isSuccess, setIsSuccess] = useState(false);

  const schema = baseForgotPasswordSchema;

  const fields = makeFormFields(["email"]);

  const forgotPasswordMutation = useForgotPasswordMutation({
    onSuccess: (data: any) => {
      setIsSuccess(true);
      onSuccess?.(data);
    },
    schema,
    onError,
  });

  const { form, handleSubmit } = useForm({
    schema,
    onSubmit: forgotPasswordMutation.mutateAsync,
  });

  const { isSubmitting, isLoading, isValid } = form.formState;

  return (
    <AuthContent
      title={isSuccess ? "Check your email" : "Forgot your password?"}
      description={
        isSuccess
          ? "We sent a password reset link to your email address."
          : "No worries, we'll send you reset instructions."
      }
    >
      {isSuccess ? (
        <div className="flex flex-col items-center justify-center gap-2">
          <Type variant="h5">Didn't recieve the email?</Type>
          <Button variant="link" onClick={handleSubmit} className="p-0">
            Click to resend.
          </Button>
        </div>
      ) : (
        <>
          <ForgotYourPasswordForm fields={fields} form={form} />
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting || isLoading || !isValid}
            type="submit"
          >
            Resend Password
          </Button>
        </>
      )}
      <Button variant="link" iconComponent={ArrowLeft} onClick={onCancel}>
        Back to Login
      </Button>
    </AuthContent>
  );
}

export default ForgotPasswordContainer;
